const CALLBACK_EVENT = 'biometric_finished'

function sessionId(value) {
  return typeof value === 'string' && /^[A-Za-z0-9_-]{1,256}$/.test(value) ? value : ''
}

export function createBiometricCallbackPayload(query = {}, storage) {
  let storedSessionId = ''
  let storedFlowToken = ''
  try {
    storedSessionId = sessionId(storage.getItem('biometricSessionId'))
    storedFlowToken = storage.getItem('biometricFlowToken') || ''
  } catch {
    // Native apps can own the session even when browser storage is unavailable.
  }

  const callbackSessionId = sessionId(query.session_id) || storedSessionId
  // Never pair a session supplied by the URL with another session's secret.
  const flowToken = callbackSessionId && callbackSessionId === storedSessionId ? storedFlowToken : ''
  const resultQuery = new URLSearchParams({ session_id: callbackSessionId })
  if (flowToken) resultQuery.set('flow_token', flowToken)

  return {
    type: CALLBACK_EVENT,
    event: CALLBACK_EVENT,
    // A provider return URL is a notification, not evidence of verification.
    // Consumers must retrieve and validate the result through the API.
    status: typeof query.status === 'string' && query.status ? query.status : 'FINISHED',
    verification_required: true,
    flow_session_result: true,
    flow_result: true,
    session_id: callbackSessionId,
    flow_token: flowToken,
    result_url: callbackSessionId ? `/api/biometric/result/?${resultQuery.toString()}` : ''
  }
}

export function sendBiometricFlowCallback({ query, storage, windowRef }) {
  const payload = createBiometricCallbackPayload(query, storage)
  const message = JSON.stringify(payload)

  // Keep the native WebView bridge contract; these channels belong to the app.
  for (const channelName of [
    'BiometricChannel', 'BiometricBridge', 'EmdeuBiometric', 'EmdeuBridge',
    'Emdeu', 'Qamqor', 'Flutter', 'ReactNativeWebView'
  ]) {
    try {
      windowRef[channelName]?.postMessage?.(message)
    } catch {
      // Native bridge availability differs between WebView implementations.
    }
  }

  // Browser windows only receive an unprivileged notification. Even a trusted
  // same-origin recipient must obtain session credentials through its own flow.
  const browserMessage = JSON.stringify({
    type: CALLBACK_EVENT,
    event: CALLBACK_EVENT,
    status: 'RETURNED',
    verification_required: true
  })
  const origin = windowRef.location.origin
  if (/^https?:\/\//.test(origin)) {
    for (const target of [windowRef.parent, windowRef.opener]) {
      if (!target || target === windowRef) continue
      try {
        target.postMessage(browserMessage, origin)
      } catch {
        // Cross-origin windows are not recipients of this callback.
      }
    }
  }

  const inAppWebView = windowRef.flutter_inappwebview
  if (inAppWebView && typeof inAppWebView.callHandler === 'function') {
    for (const handlerName of [
      'biometricFinished', 'onBiometricFinished', 'kycFinished',
      'onKycFinished', 'flowFinished', 'onFlowFinished'
    ]) {
      try {
        inAppWebView.callHandler(handlerName, payload)
      } catch {
        // Handler names are app-defined.
      }
    }
  }
}

// Only invoke from the explicit "return to app" button. URL parameters never
// select the destination scheme, Android package or browser fallback origin.
export function openBiometricApp({ query, storage, windowRef }) {
  const payload = createBiometricCallbackPayload(query, storage)
  const params = new URLSearchParams({
    status: payload.status,
    flow_session_result: 'true',
    flow_result: 'true',
    verification_required: 'true',
    session_id: payload.session_id
  })
  const fallbackUrl = encodeURIComponent(`${windowRef.location.origin}/biometric-register?${params.toString()}`)
  // Retain the native handoff; never put the secret into the HTTP fallback.
  if (payload.flow_token) params.set('flow_token', payload.flow_token)
  const deeplink = `emdeu://biometric-register?${params.toString()}`
  const intentLink = `intent://biometric-register?${params.toString()}#Intent;scheme=emdeu;package=com.emdeu.app;S.browser_fallback_url=${fallbackUrl};end`
  windowRef.location.replace(/Android/i.test(windowRef.navigator?.userAgent || '') ? intentLink : deeplink)
}
